import { useState, useEffect, useCallback, useRef } from 'react';
import type { TenantConfig, UseTenantReturn } from '@/types/tenant';
import { publicTenantApi, applyThemeCssVars, applyTenantAnalytics, type PublicTenant } from '@/services/publicTenant.service';
import { getFallbackTenantConfig } from '@/lib/tenant';
import { setCurrentTenantId } from '@/lib/currentTenant';

/**
 * Adapt the API-public tenant payload to the existing TenantConfig shape so
 * the rest of the app keeps working unchanged.
 */
function publicTenantToConfig(t: PublicTenant): TenantConfig {
  return {
    id: t.id,
    name: t.name,
    slug: t.slug,
    logo: t.logo || '',
    favicon: t.favicon || '',
    primaryColor: t.theme.primaryColor,
    secondaryColor: t.theme.secondaryColor,
    accentColor: t.theme.accentColor,
    contact: {
      phone: t.contact.phone || '',
      email: t.contact.email || '',
      address: t.contact.address || '',
      whatsapp: t.contact.whatsapp || undefined,
    },
    social: {
      facebook: t.social.facebook || undefined,
      instagram: t.social.instagram || undefined,
      linkedin: t.social.linkedin || undefined,
      youtube: t.social.youtube || undefined,
      twitter: undefined,
    },
    seo: {
      title: t.seo.title || t.name,
      description: t.seo.description || t.tagline || '',
      keywords: t.seo.keywords || [],
      ogImage: t.seo.ogImage || t.logo || '',
    },
    features: {
      blog: t.features.blogEnabled,
      multiLanguage: (t.languages?.length || 1) > 1,
      propertyComparison: t.features.propertyComparisonEnabled,
      virtualTours: t.features.virtualToursEnabled,
    },
    currencies: t.currencies || ['USD'],
    languages: t.languages || ['es'],
    theme: {
      mode: (t.theme.themeMode as 'light' | 'dark') || 'light',
      style: t.theme.themeStyle,
      customCSS: t.theme.customCSS || undefined,
    },
  };
}

interface ExtendedUseTenantReturn extends UseTenantReturn {
  /** Full public tenant payload with page sections, featured properties, etc. */
  tenant: PublicTenant | null;
}

/**
 * Resolve tenant by:
 *   1. `?tenant=<slug>` URL override
 *   2. Current window host → API public endpoint
 *   3. Local fallback (dev mode, shows a placeholder)
 */
export function useTenant(): ExtendedUseTenantReturn {
  const [tenant, setTenant] = useState<PublicTenant | null>(null);
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const loadingRef = useRef(false);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const fetchTenant = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const t = await publicTenantApi.resolveCurrent();
      setTenant(t);
      setConfig(publicTenantToConfig(t));
      applyThemeCssVars(t.theme);
      applyTenantAnalytics(t.analytics); // #333: inyectar GA4/GTM/Pixel del tenant
      setCurrentTenantId(t.id);
      localStorage.setItem('inmocms_tenant_config', JSON.stringify({ id: t.id, slug: t.slug }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      console.warn('[tenant] API fetch failed, using fallback:', msg);
      const fallback = getFallbackTenantConfig();
      setConfig(fallback);
      setError(`Using default config (${msg})`);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  const switchTenant = useCallback(async (slugOrId: string) => {
    loadingRef.current = true;
    setLoading(true);
    try {
      const t = await publicTenantApi.getBySlug(slugOrId);
      setTenant(t);
      setConfig(publicTenantToConfig(t));
      applyThemeCssVars(t.theme);
      applyTenantAnalytics(t.analytics); // #333: inyectar GA4/GTM/Pixel del tenant
      setCurrentTenantId(t.id);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  return {
    tenant,
    config,
    loading,
    error,
    isOnline,
    refetch: fetchTenant,
    switchTenant,
  };
}
